import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Plus, Search, Edit, Trash2, Loader2, Link2, Filter, X } from 'lucide-react';
import { useEmployees, useCreateEmployee, useUpdateEmployee, useDeleteEmployee, useLinkedAppUserIds, type Employee } from '@/hooks/finance/useEmployees';
import { useActiveAppUsersForSelect } from '@/hooks/useAppUsers';
import { useDepartments } from '@/hooks/useDepartments';
import { useSystemRoles } from '@/hooks/useSystemRoles';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import PeopleLayout from './PeopleLayout';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';

const SALARY_VISIBLE_EMAIL = 'lcosta@divuslegalgroup.com';

// DRE Categories mapping
const DRE_CATEGORIES = [
  { code: '6100', label: 'CSP - Legal Team', group: 'CSP' },
  { code: '6300', label: 'CSP - Customer Success', group: 'CSP' },
  { code: '5200', label: 'CAC - Sales Team', group: 'CAC' },
  { code: '5100', label: 'CAC - Marketing', group: 'CAC' },
  { code: '7100', label: 'G&A - Management', group: 'G&A' },
  { code: '7500', label: 'G&A - Other', group: 'G&A' },
] as const;

const employeeSchema = z.object({
  full_name: z.string().min(1, 'Nome é obrigatório').max(100),
  role: z.string().min(1, 'Cargo é obrigatório'),
  department: z.string().optional(),
  business_unit: z.enum(['us', 'br', 'shared']).nullable().optional(),
  base_salary: z.coerce.number().min(0, 'Salário deve ser positivo'),
  salary_currency: z.string().min(1, 'Moeda é obrigatória'),
  hire_date: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  cost_type: z.enum(['fixed', 'variable']).optional(),
  account_code: z.string().optional(),
  app_user_id: z.string().optional(),
  salary_frequency: z.enum(['monthly', 'biweekly', 'weekly']).optional(),
  weekly_payment_type: z.enum(['fixed', 'hourly']).optional(),
  hourly_rate: z.coerce.number().min(0).optional(),
  projected_hours_per_week: z.coerce.number().min(0).max(168).optional(),
  payment_schedule_type: z.enum(['fixed_day', 'weekday', 'biweekly_days']).optional(),
  payment_day_of_month: z.coerce.number().min(1).max(31).optional(),
  payment_day_of_week: z.coerce.number().min(0).max(6).optional(),
  payment_biweekly_day1: z.coerce.number().min(1).max(31).optional(),
  payment_biweekly_day2: z.coerce.number().min(1).max(31).optional(),
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

const formatCurrency = (value: number, currency: string = 'USD') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

type StatusFilter = 'active' | 'inactive' | 'all';

export default function EmployeesPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);

  const { data: employees, isLoading } = useEmployees();
  const { data: linkedAppUserIds = [] } = useLinkedAppUserIds();
  const { users: appUsers, loading: loadingAppUsers } = useActiveAppUsersForSelect();
  const { departments } = useDepartments();
  const { roles } = useSystemRoles();
  const { data: currentAppUser } = useCurrentAppUser();
  const canSeeSalary = (currentAppUser?.email || '').toLowerCase() === SALARY_VISIBLE_EMAIL;

  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();
  const deleteEmployee = useDeleteEmployee();

  const form = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      full_name: '', role: '', department: '', business_unit: undefined,
      base_salary: 0, salary_currency: 'USD', hire_date: '', email: '', phone: '',
      cost_type: 'fixed', account_code: 'auto', app_user_id: '',
      salary_frequency: 'monthly', weekly_payment_type: undefined,
      hourly_rate: undefined, projected_hours_per_week: undefined,
      payment_schedule_type: 'fixed_day', payment_day_of_month: undefined,
      payment_day_of_week: undefined, payment_biweekly_day1: undefined,
      payment_biweekly_day2: undefined,
    },
  });

  const salaryFrequency = form.watch('salary_frequency');
  const weeklyPaymentType = form.watch('weekly_payment_type');
  const paymentScheduleType = form.watch('payment_schedule_type');

  const availableAppUsers = appUsers.filter(u => {
    if (editingEmployee?.app_user_id === u.id) return true;
    return !linkedAppUserIds.includes(u.id);
  });

  const getAppUserDisplayName = (user: typeof appUsers[0]) => {
    if (user.display_name) return user.display_name;
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : user.email || 'Sem nome';
  };

  const getAppUserById = (id: string | null) => {
    if (!id) return null;
    return appUsers.find(u => u.id === id);
  };

  // Active filter count
  const activeFilterCount = [
    businessUnitFilter !== 'all',
    departmentFilter !== 'all',
    roleFilter !== 'all',
    statusFilter !== 'active',
  ].filter(Boolean).length;

  const clearFilters = () => {
    setBusinessUnitFilter('all');
    setDepartmentFilter('all');
    setRoleFilter('all');
    setStatusFilter('active');
    setSearchTerm('');
  };

  // Apply all filters
  const filteredEmployees = employees?.filter(emp => {
    // Status
    if (statusFilter !== 'all' && emp.status !== statusFilter) return false;
    // BU
    if (businessUnitFilter !== 'all') {
      if (businessUnitFilter === 'shared' && emp.business_unit !== null) return false;
      if (businessUnitFilter !== 'shared' && emp.business_unit !== businessUnitFilter) return false;
    }
    // Department
    if (departmentFilter !== 'all' && emp.department !== departmentFilter) return false;
    // Role
    if (roleFilter !== 'all' && emp.role !== roleFilter) return false;
    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matches = emp.full_name.toLowerCase().includes(term) ||
        emp.role?.toLowerCase().includes(term) ||
        emp.department?.toLowerCase().includes(term) ||
        emp.email?.toLowerCase().includes(term);
      if (!matches) return false;
    }
    return true;
  }) || [];

  // Extract unique departments and roles from actual data for filter dropdowns
  const uniqueDepartments = [...new Set(employees?.map(e => e.department).filter(Boolean) as string[])].sort();
  const uniqueRoles = [...new Set(employees?.map(e => e.role).filter(Boolean) as string[])].sort();

  const handleOpenDialog = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      const costType = employee.cost_type === 'mixed' ? 'fixed' : (employee.cost_type || 'fixed');
      const biweeklyDays = employee.payment_biweekly_days || [];
      form.reset({
        full_name: employee.full_name, role: employee.role || '',
        department: employee.department || '', business_unit: employee.business_unit,
        base_salary: employee.base_salary || 0, salary_currency: employee.salary_currency || 'USD',
        hire_date: employee.hire_date || '', email: employee.email || '', phone: employee.phone || '',
        cost_type: costType as 'fixed' | 'variable', account_code: employee.account_code || 'auto',
        app_user_id: employee.app_user_id || '',
        salary_frequency: employee.salary_frequency || 'monthly',
        weekly_payment_type: employee.weekly_payment_type || undefined,
        hourly_rate: employee.hourly_rate || undefined,
        projected_hours_per_week: employee.projected_hours_per_week || undefined,
        payment_schedule_type: employee.payment_schedule_type || 'fixed_day',
        payment_day_of_month: employee.payment_day_of_month || undefined,
        payment_day_of_week: employee.payment_day_of_week || undefined,
        payment_biweekly_day1: biweeklyDays[0] || undefined,
        payment_biweekly_day2: biweeklyDays[1] || undefined,
      });
    } else {
      setEditingEmployee(null);
      form.reset({
        full_name: '', role: '', department: '', business_unit: undefined,
        base_salary: 0, salary_currency: 'USD', hire_date: '', email: '', phone: '',
        cost_type: 'fixed', account_code: 'auto', app_user_id: '',
        salary_frequency: 'monthly', weekly_payment_type: undefined,
        hourly_rate: undefined, projected_hours_per_week: undefined,
        payment_schedule_type: 'fixed_day', payment_day_of_month: undefined,
        payment_day_of_week: undefined, payment_biweekly_day1: undefined,
        payment_biweekly_day2: undefined,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingEmployee(null);
    form.reset();
  };

  const onSubmit = async (data: EmployeeFormData) => {
    try {
      const businessUnitToSave = data.business_unit === 'shared' ? null : (data.business_unit || null);
      const appUserIdToSave = data.app_user_id === '' || data.app_user_id === 'none' ? null : data.app_user_id;

      const paymentBiweeklyDays = data.payment_schedule_type === 'biweekly_days' && data.payment_biweekly_day1 && data.payment_biweekly_day2
        ? [data.payment_biweekly_day1, data.payment_biweekly_day2]
        : null;

      let syncedData = { ...data };
      if (appUserIdToSave) {
        const linkedUser = getAppUserById(appUserIdToSave);
        if (linkedUser) {
          syncedData.full_name = getAppUserDisplayName(linkedUser);
          syncedData.email = linkedUser.email || data.email;
        }
      }

      const frequencyFields = {
        salary_frequency: data.salary_frequency || 'monthly',
        weekly_payment_type: data.salary_frequency === 'weekly' ? (data.weekly_payment_type || null) : null,
        hourly_rate: data.salary_frequency === 'weekly' && data.weekly_payment_type === 'hourly' ? (data.hourly_rate || null) : null,
        projected_hours_per_week: data.salary_frequency === 'weekly' && data.weekly_payment_type === 'hourly' ? (data.projected_hours_per_week || null) : null,
        payment_schedule_type: data.payment_schedule_type || 'fixed_day',
        payment_day_of_month: data.payment_schedule_type === 'fixed_day' ? (data.payment_day_of_month || null) : null,
        payment_day_of_week: data.payment_schedule_type === 'weekday' ? (data.payment_day_of_week || null) : null,
        payment_biweekly_days: paymentBiweeklyDays,
      };

      if (editingEmployee) {
        await updateEmployee.mutateAsync({
          id: editingEmployee.id, ...syncedData,
          business_unit: businessUnitToSave,
          account_code: data.account_code === 'auto' ? null : data.account_code || null,
          app_user_id: appUserIdToSave, ...frequencyFields,
        });
        toast.success('Colaborador atualizado com sucesso!');
      } else {
        await createEmployee.mutateAsync({
          full_name: syncedData.full_name, role: syncedData.role,
          base_salary: syncedData.base_salary, department: syncedData.department,
          business_unit: businessUnitToSave, salary_currency: syncedData.salary_currency,
          hire_date: syncedData.hire_date, email: syncedData.email,
          cost_type: syncedData.cost_type || 'fixed',
          account_code: data.account_code === 'auto' ? undefined : data.account_code || undefined,
          app_user_id: appUserIdToSave, ...frequencyFields,
        } as any);
        toast.success('Colaborador cadastrado com sucesso!');
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('Erro ao salvar colaborador:', error);
      toast.error(error?.message || 'Erro ao salvar colaborador.');
    }
  };

  const handleDelete = async () => {
    if (!deletingEmployee) return;
    try {
      await deleteEmployee.mutateAsync(deletingEmployee.id);
      toast.success('Colaborador desativado com sucesso!');
      setDeletingEmployee(null);
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao desativar colaborador.');
    }
  };

  const isSubmitting = createEmployee.isPending || updateEmployee.isPending;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <PeopleLayout>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Colaboradores</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredEmployees.length} colaborador{filteredEmployees.length !== 1 ? 'es' : ''} encontrado{filteredEmployees.length !== 1 ? 's' : ''}
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={(open) => open ? handleOpenDialog() : handleCloseDialog()}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Novo Colaborador
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingEmployee ? 'Editar Colaborador' : 'Novo Colaborador'}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 py-4">
                {/* Dados Pessoais */}
                <h4 className="text-sm font-medium text-muted-foreground border-b pb-2">Dados Pessoais</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="full_name" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome Completo *</FormLabel>
                      <FormControl><Input placeholder="Nome completo" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="email" render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mail</FormLabel>
                      <FormControl><Input type="email" placeholder="email@exemplo.com" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                {/* Cargo e Departamento */}
                <h4 className="text-sm font-medium text-muted-foreground border-b pb-2 mt-2">Posição</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="role" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cargo *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione o cargo" /></SelectTrigger></FormControl>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.id} value={role.name}>{role.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="department" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Departamento</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                        <SelectContent>
                          {departments.map((dept) => (
                            <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="business_unit" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Unit</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || undefined}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="us">🇺🇸 USA</SelectItem>
                          <SelectItem value="br">🇧🇷 Brasil</SelectItem>
                          <SelectItem value="shared">🔗 Compartilhado</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="hire_date" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Admissão</FormLabel>
                      <FormControl><Input type="date" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                {/* Remuneração */}
                <h4 className="text-sm font-medium text-muted-foreground border-b pb-2 mt-2">Remuneração</h4>
                <FormField control={form.control} name="cost_type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Custo</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="fixed">Fixo (sem comissão)</SelectItem>
                        <SelectItem value="variable">Com Comissão</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                {canSeeSalary && (
                <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="salary_frequency" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Frequência de Pagamento *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || 'monthly'}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="monthly">Mensal</SelectItem>
                          <SelectItem value="biweekly">Quinzenal</SelectItem>
                          <SelectItem value="weekly">Semanal</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                  {salaryFrequency === 'weekly' && (
                    <FormField control={form.control} name="weekly_payment_type" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Pagamento</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || 'fixed'}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="fixed">Valor Fixo</SelectItem>
                            <SelectItem value="hourly">Por Hora</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}
                </div>

                <div className={`grid gap-4 ${salaryFrequency === 'weekly' && weeklyPaymentType === 'hourly' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  {salaryFrequency === 'weekly' && weeklyPaymentType === 'hourly' ? (
                    <>
                      <FormField control={form.control} name="hourly_rate" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Valor/Hora *</FormLabel>
                          <FormControl><Input type="number" step="0.01" placeholder="25.00" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="projected_hours_per_week" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Horas/Semana *</FormLabel>
                          <FormControl><Input type="number" step="0.5" placeholder="40" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="salary_currency" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Moeda *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl><SelectTrigger><SelectValue placeholder="Moeda" /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="BRL">BRL</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </>
                  ) : (
                    <>
                      <FormField control={form.control} name="base_salary" render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {salaryFrequency === 'weekly' ? 'Valor Semanal *' :
                             salaryFrequency === 'biweekly' ? 'Valor por Quinzena *' : 'Salário Mensal *'}
                          </FormLabel>
                          <FormControl><Input type="number" placeholder="5000" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="salary_currency" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Moeda *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl><SelectTrigger><SelectValue placeholder="Moeda" /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="BRL">BRL</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </>
                  )}
                </div>
                </>
                )}

                {/* DRE & System User */}
                <h4 className="text-sm font-medium text-muted-foreground border-b pb-2 mt-2">Configurações</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="account_code" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria DRE</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || 'auto'}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="auto">Automático (baseado no cargo)</SelectItem>
                          {DRE_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.code} value={cat.code}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="app_user_id" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1">
                        <Link2 className="h-3 w-3" />
                        Usuário do Sistema
                      </FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          if (value && value !== 'none') {
                            const selectedUser = getAppUserById(value);
                            if (selectedUser) {
                              form.setValue('full_name', getAppUserDisplayName(selectedUser));
                              if (selectedUser.email) form.setValue('email', selectedUser.email);
                            }
                          }
                        }}
                        value={field.value || 'none'}
                        disabled={loadingAppUsers}
                      >
                        <FormControl><SelectTrigger><SelectValue placeholder="Vincular a um usuário" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="none">Nenhum (sem acesso ao sistema)</SelectItem>
                          {availableAppUsers.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {getAppUserDisplayName(user)} {user.email ? `(${user.email})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                {/* Payment Schedule */}
                <h4 className="text-sm font-medium text-muted-foreground border-b pb-2 mt-2">Data de Pagamento</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="payment_schedule_type" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Data</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || 'fixed_day'}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="fixed_day">Dia Fixo do Mês</SelectItem>
                          <SelectItem value="weekday">Dia da Semana</SelectItem>
                          <SelectItem value="biweekly_days">Duas Datas no Mês</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                  {paymentScheduleType === 'fixed_day' && (
                    <FormField control={form.control} name="payment_day_of_month" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dia do Mês (1-31)</FormLabel>
                        <FormControl><Input type="number" min={1} max={31} placeholder="15" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}
                  {paymentScheduleType === 'weekday' && (
                    <FormField control={form.control} name="payment_day_of_week" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dia da Semana</FormLabel>
                        <Select onValueChange={(v) => field.onChange(Number(v))} value={field.value?.toString() || undefined}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="1">Segunda-feira</SelectItem>
                            <SelectItem value="2">Terça-feira</SelectItem>
                            <SelectItem value="3">Quarta-feira</SelectItem>
                            <SelectItem value="4">Quinta-feira</SelectItem>
                            <SelectItem value="5">Sexta-feira</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}
                </div>
                {paymentScheduleType === 'biweekly_days' && (
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="payment_biweekly_day1" render={({ field }) => (
                      <FormItem>
                        <FormLabel>1º Dia do Mês</FormLabel>
                        <FormControl><Input type="number" min={1} max={31} placeholder="15" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="payment_biweekly_day2" render={({ field }) => (
                      <FormItem>
                        <FormLabel>2º Dia do Mês</FormLabel>
                        <FormControl><Input type="number" min={1} max={31} placeholder="30" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancelar</Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingEmployee ? 'Atualizar' : 'Salvar'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, cargo, e-mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">✅ Ativos</SelectItem>
            <SelectItem value="inactive">⏸️ Inativos</SelectItem>
            <SelectItem value="all">📋 Todos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={businessUnitFilter} onValueChange={setBusinessUnitFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Business Unit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">🌐 Todas as BUs</SelectItem>
            <SelectItem value="us">🇺🇸 USA</SelectItem>
            <SelectItem value="br">🇧🇷 Brasil</SelectItem>
            <SelectItem value="shared">🔗 Compartilhado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Departamento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Departamentos</SelectItem>
            {uniqueDepartments.map((dept) => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Cargo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Cargos</SelectItem>
            {uniqueRoles.map((role) => (
              <SelectItem key={role} value={role}>{role}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
            <X className="h-4 w-4 mr-1" />
            Limpar filtros ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Departamento</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>BU</TableHead>
                  <TableHead>Admissão</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Salário</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                      Nenhum colaborador encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((employee) => {
                    const linkedUser = getAppUserById(employee.app_user_id);
                    return (
                      <TableRow key={employee.id} className={employee.status === 'inactive' ? 'opacity-60' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{employee.full_name}</span>
                            {linkedUser && (
                              <Badge
                                variant="outline"
                                className="border-blue-200 text-blue-600 bg-blue-50 cursor-pointer hover:bg-blue-100 text-xs"
                                onClick={() => navigate(createPageUrl('UserDetail') + `?id=${employee.app_user_id}`)}
                              >
                                <Link2 className="h-3 w-3 mr-1" />
                                Vinculado
                              </Badge>
                            )}
                          </div>
                          {employee.email && (
                            <p className="text-xs text-muted-foreground mt-0.5">{employee.email}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          {employee.department ? (
                            <Badge variant="outline" className="font-normal">
                              {employee.department}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{employee.role || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            employee.business_unit === 'us'
                              ? 'border-blue-200 bg-blue-50 text-blue-700'
                              : employee.business_unit === 'br'
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : 'border-muted'
                          }>
                            {employee.business_unit === 'us' ? '🇺🇸 US' : employee.business_unit === 'br' ? '🇧🇷 BR' : '🌐'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(employee.hire_date)}</TableCell>
                        <TableCell>
                          <Badge variant={employee.status === 'active' ? 'default' : 'secondary'}
                            className={employee.status === 'active'
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200'
                              : 'bg-muted text-muted-foreground'
                            }
                          >
                            {employee.status === 'active' ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {canSeeSalary ? formatCurrency(employee.base_salary, employee.salary_currency) : '***'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(employee)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {employee.status === 'active' && (
                              <Button variant="ghost" size="icon" onClick={() => setDeletingEmployee(employee)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingEmployee} onOpenChange={() => setDeletingEmployee(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar Colaborador</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desativar {deletingEmployee?.full_name}?
              Esta ação irá marcar o colaborador como inativo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteEmployee.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Desativar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PeopleLayout>
  );
}
